/**
 * the reference design "Select size" quick-add picker.
 *
 * One instance per document (`snippets/giesto-store-size-picker.liquid`,
 * rendered from the layout next to the toast), populated on demand from
 * whichever card was tapped. Nothing on a collection card navigates to the PDP
 * to pick a variant any more.
 *
 * WIRING — the listener is delegated from `document`, not bound per card. The
 * collection grid is replaced wholesale by the Section Rendering API on every
 * filter, and appended to by infinite scroll; a card that arrives either way is
 * live immediately because nothing was ever bound to it.
 *
 * SHELL — the drawer is the theme's own `<theme-drawer>`, so focus trapping,
 * Escape, scroll lock and the modal (bottom sheet) / sidebar split at 990px all
 * come from `assets/theme-drawer.js`. Two gaps are filled here: an outside
 * click at desktop width, where the dialog is non-modal and therefore has no
 * `::backdrop` for the theme's own handler to catch, and returning focus to the
 * exact `+` that opened the picker.
 *
 * CART — this file deliberately contains no cart client. `giesto-store-toast`
 * already owns the one on the reference design templates, including the `sections` piggyback
 * and the `CartLinesUpdateEvent` / `CartErrorEvent` contract, and the toast is
 * what has to appear afterwards anyway. Its `addVariant()` is called here.
 */

const TRIGGER_SELECTOR = '[data-gac-size-trigger]';
const ROW_SELECTOR = '[data-gs-size-add]';
const CARD_SELECTOR = '.gac-card, [data-gs-size-source]';
const TOAST_TAG = 'giesto-store-toast';
const DRAWER_TAG = 'theme-drawer';
const OPEN_EVENT = 'giesto:size-picker:open';

/** How long to wait for a custom element module before giving up on it. */
const UPGRADE_TIMEOUT_MS = 5000;

/**
 * Resolves when a custom element is defined, or rejects if its module never
 * lands. Without the timeout a missing asset would leave a row spinning
 * forever instead of surfacing the error copy.
 *
 * @param {string} tagName
 * @returns {Promise<unknown>}
 */
function whenDefined(tagName) {
  return Promise.race([
    customElements.whenDefined(tagName),
    new Promise((_resolve, reject) => {
      setTimeout(() => reject(new Error(`<${tagName}> did not upgrade`)), UPGRADE_TIMEOUT_MS);
    }),
  ]);
}

/**
 * Reads a card's variant payload.
 *
 * @param {Element | null} script - The card's `<script type="application/json">`.
 * @returns {Array<{ id?: number | string, available?: boolean, options?: string[] }>}
 */
function readVariants(script) {
  if (!(script instanceof HTMLScriptElement)) return [];

  try {
    const parsed = JSON.parse(script.textContent || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const syncSizePickerScrim = () => {
  document.documentElement.classList.toggle('gs-size-picker-open', Boolean(document.querySelector('.gs-size__dialog[open]')));
};

class GiestoStoreSizePicker extends HTMLElement {
  #scrimObserver;
  /** @type {AbortController | null} */
  #abortController = null;

  /** The `+` that opened the picker, and the element focus goes back to. */
  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #returnFocus = null;

  /** Guards against a double-tap posting the same line twice. */
  #busy = false;

  connectedCallback() {
    // Re-entrant by contract: a move in the DOM fires disconnect + connect, and
    // the theme's morph can reconnect this element without a disconnect at all.
    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Safari can retain :focus-visible when the drawer focuses its close button
    // after a tap. Keep that automatic focus, but show its ring only for keyboard use.
    document.addEventListener('pointerdown', () => this.setAttribute('data-pointer-focus', ''), { signal, capture: true });
    document.addEventListener('keydown', () => this.removeAttribute('data-pointer-focus'), { signal, capture: true });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener(OPEN_EVENT, this.#onOpenRequest, { signal });
    this.addEventListener('theme-drawer:close', this.#onDrawerClose, { signal });
    this.#scrimObserver = new MutationObserver(syncSizePickerScrim);
    this.#scrimObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
    syncSizePickerScrim();
  }

  disconnectedCallback() {
    this.#scrimObserver?.disconnect();
    this.#scrimObserver = null;
    syncSizePickerScrim();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#trigger = null;
    this.#returnFocus = null;
    this.#busy = false;
  }

  /* ---------------------------------------------------------------- refs -- */

  /** @returns {(HTMLElement & { open?: () => void, close?: () => void }) | null} */
  get #drawer() {
    return this.querySelector(DRAWER_TAG);
  }

  /** @returns {HTMLElement | null} */
  get #panel() {
    return this.querySelector('dialog');
  }

  /** @returns {HTMLElement | null} */
  get #list() {
    return this.querySelector('[data-gs-size-list]');
  }

  /** @returns {HTMLTemplateElement | null} */
  get #rowTemplate() {
    const template = this.querySelector('[data-gs-size-row-template]');
    return template instanceof HTMLTemplateElement ? template : null;
  }

  get #isOpen() {
    return this.#drawer?.hasAttribute('open') ?? false;
  }

  /* -------------------------------------------------------------- clicks -- */

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    // Element, not HTMLElement — a click landing on the trigger's inline <svg>
    // or a <path> is an SVGElement, and an HTMLElement check would swallow it.
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger instanceof HTMLElement) {
      event.preventDefault();
      this.#openFor(trigger);
      return;
    }

    const panel = this.#panel;
    if (!panel || !this.#isOpen) return;

    const row = target.closest(ROW_SELECTOR);
    if (row instanceof HTMLButtonElement && panel.contains(row)) {
      this.#add(row);
      return;
    }

    // Outside click. Below 990px the panel is a modal dialog and theme-drawer.js
    // closes it from its own backdrop handler — a backdrop click targets the
    // <dialog> itself, which `contains` treats as inside, so the two never both
    // fire. At desktop width the dialog is non-modal, has no ::backdrop at all,
    // and this is the only thing that can dismiss it by pointer.
    if (panel.contains(target)) return;
    this.#close();
  };

  /** @param {Event} event */
  #onOpenRequest = (event) => {
    const detail = event instanceof CustomEvent && event.detail && typeof event.detail === 'object' ? event.detail : {};
    const trigger = detail.trigger;
    const returnFocus = detail.returnFocus;

    if (!(trigger instanceof HTMLElement)) return;

    this.#openFor(trigger, returnFocus instanceof HTMLElement ? returnFocus : null);
  };

  /* ---------------------------------------------------------------- open -- */

  /** @param {HTMLElement} trigger @param {HTMLElement | null} [returnFocus] */
  #openFor(trigger, returnFocus = null) {
    // A size choice opened from the editorial modal must enter the top layer
    // too; a desktop non-modal drawer would otherwise sit behind its parent.
    const fromLookbook = Boolean(trigger.closest('.ga-lookbook-drawer, .geh-menu'));
    const fromCart = Boolean(trigger.closest('.ghs-cart'));
    const fromSplash = Boolean(trigger.closest('[data-giesto-splash-dialog]'));
    this.#drawer?.toggleAttribute('data-splash', fromSplash);
    this.#drawer?.toggleAttribute('data-cart', fromCart);
    this.#drawer?.toggleAttribute('data-lookbook', fromLookbook);
    this.#drawer?.toggleAttribute('data-mobile-product-buy', trigger.hasAttribute('data-mobile-product-buy'));
    this.#drawer?.toggleAttribute('modal-always', fromLookbook || fromCart || fromSplash);
    const card = trigger.closest(CARD_SELECTOR);
    const variants = readVariants(card?.querySelector('script[data-gac-variants]') ?? null);

    const parsedPosition = Number.parseInt(trigger.dataset.sizePosition ?? '', 10);
    const sizePosition = Number.isInteger(parsedPosition) && parsedPosition >= 0 ? parsedPosition : 0;

    const title = trigger.dataset.productTitle ?? '';
    const url = trigger.dataset.productUrl ?? '';

    this.dataset.productId = trigger.dataset.productId ?? '';
    this.dataset.productHandle = trigger.dataset.productHandle ?? '';
    this.toggleAttribute('data-size-reminders', trigger.hasAttribute('data-gac-reminders'));

    this.#setError('');
    this.#renderRows(variants, sizePosition, fromSplash);

    const heading = this.querySelector('.gs-size__title');
    if (heading instanceof HTMLElement) {
      heading.dataset.defaultText ??= heading.textContent.trim();
      heading.textContent = trigger.dataset.sizePickerTitle || heading.dataset.defaultText;
    }

    const product = this.querySelector('[data-gs-size-product]');
    if (product) product.textContent = title;

    const link = this.querySelector('[data-gs-size-link]');
    if (link instanceof HTMLAnchorElement && url) link.href = url;

    this.#setTrigger(trigger);
    this.#returnFocus = returnFocus && document.contains(returnFocus) ? returnFocus : trigger;

    // Focused BEFORE the drawer opens on purpose: `ThemeDrawer#open` captures
    // `document.activeElement` as the element it hands focus back to on close.
    // Safari does not focus a <button> on click, so without this the theme's
    // restore would fall back to whatever else was focused.
    this.#returnFocus.focus({ preventScroll: true });
    this.#openDrawer();
  }

  /**
   * Builds one row per distinct size value, in server order.
   *
   * @param {Array<{ id?: number | string, available?: boolean, options?: string[] }>} variants
   * @param {number} sizePosition - Which option position holds the size, from the card.
   * @param {boolean} [fullVariantLabel] - Match the homepage drawer's colour / size labels.
   */
  #renderRows(variants, sizePosition, fullVariantLabel = false) {
    const list = this.#list;
    const template = this.#rowTemplate;
    if (!list || !template) return;

    /** @type {Map<string, { id?: number | string, available?: boolean }>} */
    const rows = new Map();

    for (const variant of variants) {
      const options = Array.isArray(variant?.options) ? variant.options : [];
      // A product whose size lives past the end of its option list should still
      // label its rows with something real, so fall back to the last option.
      const label = fullVariantLabel
        ? options.map(option => String(option).trim()).filter(Boolean).join(' / ')
        : String(options[sizePosition] ?? options[options.length - 1] ?? '').trim();
      if (!label) continue;

      const existing = rows.get(label);
      // First one wins, except that a sellable variant always beats a sold-out
      // one carrying the same label.
      if (existing && !(existing.available !== true && variant.available === true)) continue;

      rows.set(label, variant);
    }

    const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    const fragment = document.createDocumentFragment();

    for (const [label, variant] of rows) {
      const row = template.content.querySelector('.gs-size__row')?.cloneNode(true);
      if (!(row instanceof HTMLElement)) continue;

      const labelElement = row.querySelector('[data-gs-size-label]');
      if (labelElement) labelElement.textContent = label;

      const button = row.querySelector(ROW_SELECTOR);
      if (button instanceof HTMLButtonElement) {
        const available = variant.available === true && variant.id != null;

        button.disabled = !available;
        button.dataset.variantId = available ? String(variant.id) : '';
        if (!available && variant.id != null && this.hasAttribute('data-size-reminders')) {
          row.dataset.reminderContext = '';
          row.dataset.productId = this.dataset.productId;
          row.dataset.productHandle = this.dataset.productHandle;
          row.dataset.variantId = String(variant.id);
          row.dataset.reminderName = label;
          button.removeAttribute('data-gs-size-add');
          button.dataset.reminderToggle = '';
          button.setAttribute('aria-pressed', 'false');
          const icon = this.querySelector('[data-reminder-bell-template]');
          if (icon instanceof HTMLTemplateElement) button.append(icon.content.cloneNode(true));
          const state = document.createElement('span'); state.className = 'gs-size__sr'; state.dataset.reminderLabel = ''; button.append(state);
        }

        const note = row.querySelector('[data-gs-size-note]');
        // The line through a sold-out size is not announced, so say it.
        if (note) note.textContent = available ? '' : ` ${soldOutLabel}`;
      }

      fragment.append(row);
    }

    list.replaceChildren(fragment);
    document.dispatchEvent(new CustomEvent('giesto:reminders:scan'));
  }

  /** @param {HTMLElement} trigger */
  #setTrigger(trigger) {
    if (this.#trigger && this.#trigger !== trigger) this.#trigger.setAttribute('aria-expanded', 'false');

    this.#trigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
  }

  #openDrawer() {
    const drawer = this.#drawer;
    if (!drawer) return;

    // Same upgrade race the toast handles for the cart drawer: `component.js`
    // only rescues tags ending in `-component`, and `<theme-drawer>` does not,
    // so the element can still be un-upgraded while its module loads.
    if (typeof drawer.open !== 'function') customElements.upgrade(drawer);

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    whenDefined(DRAWER_TAG)
      .then(() => {
        if (this.#abortController?.signal.aborted) return;
        customElements.upgrade(drawer);
        if (typeof drawer.open === 'function' && this.#trigger) drawer.open();
      })
      .catch(() => {
        // The drawer module never arrived. Leave the card as it was rather than
        // navigating: the card's title and image are already links to the PDP.
        this.#trigger?.setAttribute('aria-expanded', 'false');
        this.#trigger = null;
        this.#returnFocus = null;
      });
  }

  #close() {
    const drawer = this.#drawer;
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (event.target !== this.#drawer) return;

    this.#setError('');

    const trigger = this.#trigger;
    const returnFocus = this.#returnFocus;
    this.#trigger = null;
    this.#returnFocus = null;
    if (!trigger) return;

    // Focus BEFORE clearing `aria-expanded`. At desktop width the row-3 box the
    // trigger sits in is `display: none` unless the card is hovered, focused
    // within, or flagged open by that attribute — and `.focus()` on a
    // display:none element is a silent no-op. Once focus lands, `:focus-within`
    // holds the box open in its place.
    //
    // Below 990px the dialog is still modal at this point, so this call is
    // ignored and `ThemeDrawer#close` restores focus once it has closed. That
    // is fine: the box is always visible there.
    const focusTarget = returnFocus && document.contains(returnFocus) ? returnFocus : trigger;
    focusTarget.focus({ preventScroll: true });
    trigger.setAttribute('aria-expanded', 'false');
  };

  /* ----------------------------------------------------------------- add -- */

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (this.#busy || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#busy = true;
    button.dataset.busy = 'true';
    this.#setError('');

    try {
      const cart = await this.#cartClient();
      await cart.addVariant({
        variantId,
        quantity: 1,
        productId: this.dataset.productId || null,
        source: button,
      });

      // The toast takes it from here — it is listening for the very
      // CartLinesUpdateEvent that `addVariant` dispatched.
      this.#close();
    } catch {
      this.#setError(this.dataset.errorLabel || 'Error adding to cart');
    } finally {
      this.#busy = false;
      delete button.dataset.busy;
    }
  }

  /**
   * The page's one cart client, on the toast element.
   *
   * @returns {Promise<HTMLElement & { addVariant: (options: object) => Promise<void> }>}
   */
  async #cartClient() {
    const toast = /** @type {any} */ (document.querySelector(TOAST_TAG));
    if (!toast) throw new Error(`<${TOAST_TAG}> is not on the page`);

    if (typeof toast.addVariant !== 'function') customElements.upgrade(toast);

    if (typeof toast.addVariant !== 'function') {
      await whenDefined(TOAST_TAG);
      customElements.upgrade(toast);
    }

    if (typeof toast.addVariant !== 'function') throw new Error(`<${TOAST_TAG}> exposes no cart client`);

    return toast;
  }

  /** @param {string} message */
  #setError(message) {
    const target = this.querySelector('[data-gs-size-error]');
    if (!(target instanceof HTMLElement)) return;

    if (!message) {
      target.textContent = '';
      target.setAttribute('hidden', '');
      return;
    }

    // Unhidden BEFORE the text is written: a `role="alert"` that is still
    // `display: none` when its content changes is not announced by every
    // screen reader.
    target.removeAttribute('hidden');
    target.textContent = message;
  }
}

if (!customElements.get('giesto-store-size-picker')) {
  customElements.define('giesto-store-size-picker', GiestoStoreSizePicker);
}
